import type { Dispatch, ReactNode, SetStateAction } from 'react'

import clsx from 'clsx'
import Select, {
	components,
	type GroupBase,
	type MultiValue,
	type Props
} from 'react-select'
import { useSwitcher } from 'shared/hooks'
import { typo } from 'shared/styles'

import { StackIcon } from '../StackIcon'
import { DropdownIndicator } from './DropdownIndicator'
import css from './List.module.scss'

const defaultGetLabel = (option: any) => option.label
const { Option } = components

export function ListSelect<
	Option,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>
>({
	className,
	components,
	getTag,
	isMulti,
	rootClassName,
	setter,
	value,
	...props
}: Props<Option, IsMulti, Group> & {
	getTag?: (option: Option) => ReactNode
	rootClassName?: string
	setter?: Dispatch<SetStateAction<Option[]>>
}) {
	return (
		<div className={clsx(rootClassName, css.wrapper)}>
			<Select
				className={clsx(css.root, className)}
				classNames={{
					// clearIndicator: () => css.clear,
					control: ({ isDisabled, isFocused }) =>
						clsx(css.control, typo.buttonSm, {
							[css.controlDisabled]: isDisabled,
							[css.controlFocused]: isFocused
						}),
					indicatorsContainer: () => css.indicatorsContainer,
					// dropdownIndicator: () => css.indicator,
					input: () => css.input,

					menu: () => css.menu,
					menuList: () => clsx(css.list, 'app-scroll-thin'),
					multiValue: () => css.multi,

					option: ({ isFocused, isSelected }) =>
						clsx(css.option, {
							[css.focusedOption]: isFocused,
							[css.selectedOption]: isSelected
						}),
					valueContainer: () => css.container
				}}
				components={{
					// @ts-ignore
					DropdownIndicator,
					IndicatorSeparator: null,
					MultiValueContainer: () => null,
					Option: props => (
						<Option
							{...props}
							isDisabled={props.isSelected || props.isDisabled}
						/>
					),
					...components
				}}
				hideSelectedOptions={false}
				isClearable={false}
				isMulti={isMulti}
				value={value}
				{...props}
			/>

			{isMulti && (
				<ShowedValues
					getTag={getTag}
					setter={setter}
					values={value as MultiValue<Option>}
				/>
			)}
		</div>
	)
}

function ShowedValues<Option>({
	getTag = defaultGetLabel,
	setter,
	values
}: {
	getTag?: (option: Option) => ReactNode

	setter?: Dispatch<SetStateAction<Option[]>>

	values: MultiValue<Option>
}) {
	const showAll = useSwitcher()

	const showedValues = showAll.isOn ? values : values?.slice(0, 3)

	if (!values?.length) {
		return null
	}

	const remove = (option: Option) => {
		setter?.(previous => previous.filter(item => item !== option))
	}

	const allowShowMore = !showAll.isOn && values.length > 3

	return (
		<>
			<ul className={css.tags}>
				{showedValues.map((item, index) => {
					return (
						<li className={css.tag} key={index}>
							<button
								aria-label='Remove'
								className={css.removeTag}
								onClick={() => remove(item)}
								type='button'
							>
								<StackIcon icon='close' />
							</button>
							{getTag(item)}
						</li>
					)
				})}
			</ul>

			{allowShowMore && (
				<button className={css.showMore} onClick={showAll.on} type='button'>
					Show more
				</button>
			)}
		</>
	)
}
